// createSim: one job, stepped at the renderer's frame rate.
//
// Notes for callers:
//  - Pass the BASE layout; createSim applies `contract.mutations` itself with
//    the contract seed (a layout from `contractLayout`/`applyMutations` is
//    detected and not mutated twice). `state.layout` is the mutated yard.
//  - `switchTool: 'mower'` puts the operator straight back on the mower (at
//    its parked spot); switching to a hand tool parks the mower where it is.
//  - `result(false)` before all tasks are done is treated as abandoned.
//  - ALL DONE (`state.finished`, one `all-done` event) does not stop the job:
//    the clock and the yard keep running until the host calls `result()`.
//  - JobResult.gear holds the updated OwnedEquipment (mower + tools) for
//    `applyJobResult`.
//  - dt is clamped to 0.1 s per step.

import type {
  GroundObject, Input, JobResult, ResultOptions, Sim, SimOptions, TaskKind, TaskProgress, ToolKind, OwnedEquipment,
} from './types';
import type { Ctx, ToolSlot } from './ctx';
import { buildGrid, cellAt, propHalfExtents, T_GRASS } from './layout';
import { contractLayout, type Hazards } from './mutations';
import { mulberry32, randRange } from './rng';
import { initInRange } from './cells';
import { createMowerState, stepMower, stepWalker, inShade } from './mower';
import { spawnLeaves, stepLeaves, leafCounts } from './leaves';
import { spawnObjects } from './spawn';
import { stepTrimmer } from './tools/trimmer';
import { stepBlower } from './tools/blower';
import { stepRake } from './tools/rake';
import { placeTarp, bagTarp, tarpSpec } from './tools/tarp';
import { parSeconds, starsFor, tipPct, repForStars, OBSTACLE_PENALTY_AUD, VIOLATION_PENALTY_AUD, MULCH_PENALTY_AUD } from './contract';
import { MAJOR_HIT_PENALTY_AUD } from './props';
import { CHUTE_EXTEND_M, CHUTE_R_M, chuteSign } from './chute';
import { frontOfHouse, stripeIslands, stripePattern, stripeScore, withStripeBonus } from './stripes';
import { debrisCells, OFF_PROPERTY_NUDGE_PCT } from './debris';
import { LOW_CHARGE_PCT, chargeOf, runSecondsOf, toolWearPerMin, usesCharge } from './economy';
import { isPassable } from './props';
import {
  EXTRA_MILE_AUD, EXTRA_MILE_REP, blowBackNudge, bonusAud, extraMileEarned, groundName, honestBloke, neglectedLayout,
} from './vibe';
import { fadeTracks } from './tracks';

const TOOL_ORDER: ToolKind[] = ['trimmer', 'blower', 'rake', 'tarp'];
export const TAP_REACH_M = 2.5;

export function createSim(opts: SimOptions): Sim {
  const { contract, models } = opts;
  const mutated = contractLayout(opts.layout, contract);
  const { hazards } = mutated;
  // Feature P9: a neglected yard's grass starts twice as long (same rng draws).
  const neglected = !!contract.neglected;
  const layout = neglected ? neglectedLayout(mutated.layout) : mutated.layout;
  const rng = mulberry32(contract.seed >>> 0);
  const grid = buildGrid(layout, rng);
  // Feature P9: the extra per-cell layers exist only when their option is on.
  const nCells = grid.type.length;
  if (opts.tyreTracks) grid.matted = new Uint8Array(nCells);
  if (opts.blowBack) grid.clip = new Uint8Array(nCells);
  if (opts.passableProps) grid.scatter = new Uint8Array(nCells);
  if (opts.crossStripes) grid.stripe2 = new Uint8Array(nCells);
  const gs = layout.gameScale ?? 1;
  const mowerModel = models[opts.mower.modelKey];
  if (!mowerModel || mowerModel.kind !== 'mower') throw new Error(`createSim: '${opts.mower.modelKey}' is not a mower`);
  const mowerOwned: OwnedEquipment = { ...opts.mower };
  const tools: Partial<Record<ToolKind, ToolSlot>> = { mower: { owned: mowerOwned, model: mowerModel } };
  for (const t of opts.tools) {
    const m = models[t.modelKey];
    if (m && m.kind !== 'mower' && !tools[m.kind]) tools[m.kind] = { owned: { ...t }, model: m };
  }
  // Feature P7: battery hand tools carry their charge (absent = full) when the rules are on.
  const battery = !!opts.battery;
  const toolCharge: Partial<Record<ToolKind, number>> = {};
  if (battery) {
    for (const k of TOOL_ORDER) {
      const s = tools[k];
      if (s && usesCharge(s.model)) toolCharge[k] = s.owned.chargePct = chargeOf(s.owned);
    }
  }
  // Feature P3: a walk-only job leaves the (parked) mower off the loadout.
  const walkOnly = !!opts.walkOnly;
  const loadout: ToolKind[] = walkOnly ? TOOL_ORDER.filter((k) => tools[k]) : ['mower', ...TOOL_ORDER.filter((k) => tools[k])];
  const mower = createMowerState(mowerModel, mowerOwned, layout, grid);
  // Feature P3b: on a walk-only job the stand-in mower stays at the ute, where there is one.
  if (walkOnly && layout.utePark) {
    mower.parkedX = layout.utePark.x;
    mower.parkedY = layout.utePark.y;
    mower.parkedHeading = layout.utePark.heading;
  }
  const hasGround = contract.tasks.some((t) => t.kind === 'ground-check');
  const objects = spawnObjects(layout, grid, rng, hasGround ? contract.complexity + 1 : 0, hazards.lostBall, !!opts.groundItems);
  const leaves = spawnLeaves(layout, grid, rng);
  const big = layout.locationGroup === 'ovals' || gs > 1.5;
  const ts = tarpSpec(big);
  const propExt = new Float32Array(layout.props.length * 2);
  // Feature P9: with passable props on, they never block (the mower bumps over them).
  const passable = !!opts.passableProps;
  const passIdx: number[] = [];
  layout.props.forEach((p, i) => {
    if (passable && isPassable(p)) {
      passIdx.push(i);
      return;
    }
    if (!p.solid) return;
    const [ex, ey] = propHalfExtents(p);
    propExt[i * 2] = Math.max(ex, 1e-3);
    propExt[i * 2 + 1] = Math.max(ey, 1e-3);
  });
  const sprinklerAt = randRange(rng, 15, 45);
  const magpieAt = randRange(rng, 25, 45);
  const ibisAt = randRange(rng, 20, 60);
  const mowerRadius = Math.max(0.28, mower.deckCells * grid.cellM * 0.45);
  const tasks: TaskProgress[] = contract.tasks.map((t) => ({ kind: t.kind, label: t.label, pct: 0, target: t.target, done: false }));

  const ctx: Ctx = {
    layout, contract, grid,
    op: {
      x: layout.spawn.x, y: layout.spawn.y, heading: layout.spawn.heading, speed: 0,
      tool: walkOnly ? loadout[0] ?? 'mower' : 'mower', onMower: !walkOnly, toolActive: false,
    },
    mower, loadout, objects, leaves,
    tarp: { placed: false, x: 0, y: 0, w: ts.size, h: ts.size, held: 0, capacity: ts.capacity, bagged: 0 },
    tasks,
    mowableCells: grid.mowableCount,
    inRangeCells: 0,
    trimCells: grid.trimCount,
    elapsedS: 0,
    parS: parSeconds({
      mowableCells: grid.mowableCount, trimCells: grid.trimCount, leafCount: leaves.count, cellM: grid.cellM, gameScale: gs,
      groundObjects: hasGround ? objects.length : 0, spanCells: grid.spanCells, weedCells: grid.weedCount,
    }, mowerModel, contract.tasks),
    obstacleHits: 0, violations: 0, hitLog: [], violationZones: [], heat: 0, finished: false, events: [], tick: 0,
    rng, gs, specs: contract.tasks.slice(), mowerModel, mowerOwned, tools, hazards,
    inRange: new Uint8Array(grid.type.length), inRangeTrim: 0, weedsCleared: 0,
    propContact: new Uint8Array(layout.props.length), propTouch: new Uint8Array(layout.props.length),
    inDnm: false, throttle: 0, cruiseThrottle: 0, restartT: -1,
    outOfFuel: mower.stalled, fuelUsedL: 0, collectorCount: 0,
    mowerRadius,
    chuteTouch: new Uint8Array(layout.props.length), chuteContact: new Uint8Array(layout.props.length),
    chuteSign: opts.chuteFootprint ? chuteSign(mower.dischargeSide) : 0,
    chuteR: CHUTE_R_M * gs,
    chuteLat: mowerRadius + (CHUTE_EXTEND_M - CHUTE_R_M) * gs,
    catcherUnder: new Uint32Array(leaves.count), catcherOwed: 0,
    // Feature P6: debris rules (off when absent; the game passes FEATURES.debrisRules).
    debris: !!opts.debrisRules,
    debrisCell: opts.debrisRules ? debrisCells(layout, grid) : new Uint8Array(0),
    leafRaked: new Uint8Array(opts.debrisRules ? leaves.count : 0),
    leafCaught: new Uint8Array(opts.debrisRules ? leaves.count : 0),
    battery, toolCharge, chargeWarned: {},
    groundItems: !!opts.groundItems, tyreTracks: !!opts.tyreTracks, zeroTurn: !!opts.zeroTurn, extraMile: !!opts.extraMile,
    slopeDrift: !!opts.slopeDrift, trimOvercut: !!opts.trimOvercut, passable, blowBack: !!opts.blowBack, neglected,
    trackT: opts.tyreTracks ? new Float32Array(nCells).fill(-1) : new Float32Array(0), trackList: [], trackSec: 0,
    passIdx, passOver: new Uint8Array(layout.props.length), clipList: [], ringTrimmed: 0,
    rearTouch: new Uint8Array(layout.props.length), ztPivoted: false,
    striped: new Uint8Array(opts.crossStripes ? nCells : 0),
    propExt,
    walkerRadius: 0.25,
    cellsCut: 0,
    sprinklerAt, sprinklerUntil: -1, sprinklerCells: [], magpieAt, ibisAt, ibisDone: false,
  };
  initInRange(ctx);
  updateTasks(ctx, false);

  return {
    state: ctx,
    step(dt: number, input: Input) {
      step(ctx, dt, input);
    },
    result(abandoned = false, opts?: ResultOptions) {
      const r = result(ctx, abandoned);
      return opts?.stripeBonus ? withStripeBonus(r) : r;
    },
  };
}

function step(ctx: Ctx, dtIn: number, input: Input): void {
  ctx.events.length = 0;
  ctx.tick++;
  if (!(dtIn > 0)) return;
  const dt = Math.min(dtIn, 0.1);
  const op = ctx.op;
  const m = ctx.mower;

  if (input.switchTool && input.switchTool !== op.tool && ctx.loadout.includes(input.switchTool)) {
    if (input.switchTool === 'mower') {
      op.x = m.parkedX;
      op.y = m.parkedY;
      op.heading = m.parkedHeading;
      op.onMower = true;
    } else if (op.onMower) {
      m.parkedX = op.x;
      m.parkedY = op.y;
      m.parkedHeading = op.heading;
      op.onMower = false;
      m.bladesOn = false;
      m.cruise = false;
      m.load = 0;
      m.highLoadS = 0;
      m.redLoadS = 0;
      ctx.inDnm = false;
      op.toolActive = false;
    }
    op.speed = 0;
    op.tool = input.switchTool;
    // Getting on/off the mower never counts as hitting whatever it's parked against.
    ctx.propContact.fill(2);
  }
  if (input.placeTarp) placeTarp(ctx);
  if (input.bagTarp) bagTarp(ctx);
  if (input.tapAt) tapCollect(ctx, input.tapAt.x, input.tapAt.y);

  if (op.onMower) {
    stepMower(ctx, dt, input);
    op.toolActive = m.bladesOn;
  } else {
    stepWalker(ctx, dt, input);
    op.toolActive = input.toolHeld ? useHandTool(ctx, dt) : false;
  }

  if (ctx.hazards.hotDay) {
    const walkingHard = !op.onMower && op.speed > 0.05;
    if (op.onMower) ctx.heat -= dt / 30;
    else if (inShade(ctx)) ctx.heat -= dt / 20;
    else if (walkingHard) ctx.heat += dt / 90;
    else ctx.heat -= dt / 60;
    ctx.heat = Math.min(1, Math.max(0, ctx.heat));
  }

  stepLeaves(ctx, dt);
  walkOverCollect(ctx);
  stepHazards(ctx);
  ctx.elapsedS += dt;
  if (ctx.tyreTracks) fadeTracks(ctx);
  updateTasks(ctx, true);
}

/** Runs the held hand tool; returns whether it actually ran (for op.toolActive). */
function useHandTool(ctx: Ctx, dt: number): boolean {
  const tool = ctx.op.tool;
  const slot = ctx.tools[tool];
  if (!slot || (tool !== 'trimmer' && tool !== 'blower' && tool !== 'rake')) return false;
  if (ctx.battery && !drainCharge(ctx, tool, slot, dt)) return false;
  if (slot.model.power === 'petrol') {
    if (slot.owned.fuelL <= 0) return false;
    const burn = Math.min(slot.owned.fuelL, ((slot.model.burnLph ?? 0) * dt) / 3600);
    slot.owned.fuelL = Math.max(0, slot.owned.fuelL - burn);
    ctx.fuelUsedL += burn;
    if (slot.owned.fuelL <= 0) ctx.events.push({ type: 'out-of-fuel' });
  }
  // Feature P7: powered hand tools wear per minute on the trigger, battery ×1.6.
  if (ctx.battery) slot.owned.engineCondition = Math.max(0, slot.owned.engineCondition - (toolWearPerMin(slot.model) * dt) / 60);
  if (tool === 'trimmer') stepTrimmer(ctx);
  else if (tool === 'blower') stepBlower(ctx, dt);
  else stepRake(ctx);
  return true;
}

/**
 * Feature P7: run a battery tool down by dt of trigger time (its pack, runSecondsOf, per
 * full charge). Warns once at LOW_CHARGE_PCT; flat means it won't run. Returns
 * whether the tool may run this step (always, for gear with no battery).
 */
function drainCharge(ctx: Ctx, tool: ToolKind, slot: ToolSlot, dt: number): boolean {
  if (!usesCharge(slot.model)) return true;
  const before = chargeOf(slot.owned);
  if (before <= 0) return false;
  const after = Math.max(0, before - (dt / runSecondsOf(slot.model)) * 100);
  slot.owned.chargePct = after;
  ctx.toolCharge[tool] = after;
  if (after <= LOW_CHARGE_PCT && after > 0 && !ctx.chargeWarned[tool]) {
    ctx.chargeWarned[tool] = true;
    ctx.events.push({ type: 'low-charge', tool });
  }
  if (after <= 0) ctx.events.push({ type: 'flat', tool });
  return true;
}

function collect(ctx: Ctx, o: GroundObject): void {
  o.state = 'collected';
  ctx.events.push({ type: 'object-collected', objectId: o.id });
}

function tapCollect(ctx: Ctx, tx: number, ty: number): void {
  const op = ctx.op;
  const reach = TAP_REACH_M * Math.max(1, Math.sqrt(ctx.gs));
  const tol = Math.max(0.8, ctx.grid.cellM);
  let best: GroundObject | null = null;
  let bestD = Infinity;
  for (const o of ctx.objects) {
    if (o.state !== 'ground') continue;
    if (Math.hypot(o.x - op.x, o.y - op.y) > reach) continue;
    const d = Math.hypot(o.x - tx, o.y - ty);
    if (d <= tol && d < bestD) {
      best = o;
      bestD = d;
    }
  }
  if (best) collect(ctx, best);
}

/** Walking over an object, or driving over it with blades off, picks it up. */
function walkOverCollect(ctx: Ctx): void {
  const op = ctx.op;
  if (op.onMower && ctx.mower.bladesOn) return;
  const r = op.onMower ? ctx.mowerRadius : ctx.walkerRadius + 0.3;
  for (const o of ctx.objects) {
    if (o.state === 'ground' && Math.abs(o.x - op.x) <= r && Math.abs(o.y - op.y) <= r && Math.hypot(o.x - op.x, o.y - op.y) <= r) collect(ctx, o);
  }
}

function stepHazards(ctx: Ctx): void {
  const h: Hazards = ctx.hazards;
  const t = ctx.elapsedS;
  const g = ctx.grid;
  if (h.sprinkler && ctx.sprinklerUntil < 0 && t >= ctx.sprinklerAt) {
    const sp = ctx.layout.props.find((p) => p.kind === 'sprinkler');
    let ci = sp ? cellAt(g, sp.x, sp.y) : -1;
    if (ci < 0) {
      const grass: number[] = [];
      for (let i = 0; i < g.type.length; i++) if (g.type[i] === T_GRASS) grass.push(i);
      ci = grass.length ? grass[Math.floor(ctx.rng() * grass.length)] : -1;
    }
    ctx.sprinklerUntil = t + 60;
    if (ci >= 0) {
      const cx = ci % g.w;
      const cy = Math.floor(ci / g.w);
      const half = Math.max(2, Math.round(g.w * 0.2));
      const hh = Math.max(1, Math.round(1 / g.cellM));
      for (let y = cy - hh; y <= cy + hh; y++) {
        for (let x = cx - half; x <= cx + half; x++) {
          if (x < 0 || y < 0 || x >= g.w || y >= g.h) continue;
          const i = y * g.w + x;
          if (g.type[i] !== T_GRASS) continue;
          g.wet[i] = 1;
          g.dirty.push(i);
          ctx.sprinklerCells.push(i);
        }
      }
    }
    ctx.events.push({ type: 'sprinkler-on' });
  }
  if (ctx.sprinklerUntil >= 0 && t >= ctx.sprinklerUntil && ctx.sprinklerCells.length) {
    for (const i of ctx.sprinklerCells) {
      g.wet[i] = 0;
      g.dirty.push(i);
    }
    ctx.sprinklerCells.length = 0;
  }
  if (h.magpie && t >= ctx.magpieAt) {
    ctx.events.push({ type: 'magpie', x: ctx.op.x + randRange(ctx.rng, -1.5, 1.5), y: ctx.op.y + randRange(ctx.rng, -1.5, 1.5) });
    ctx.magpieAt = t + randRange(ctx.rng, 25, 45);
  }
  if (h.ibis && !ctx.ibisDone && t >= ctx.ibisAt) {
    ctx.ibisDone = true;
    const ground = ctx.objects.filter((o) => o.state === 'ground');
    if (ground.length) {
      const o = ground[Math.floor(ctx.rng() * ground.length)];
      o.state = 'stolen';
      ctx.events.push({ type: 'ibis', objectId: o.id });
    }
  }
}

function taskPct(ctx: Ctx, kind: TaskKind, lc: ReturnType<typeof leafCounts> | null): number {
  switch (kind) {
    case 'mow':
      return ctx.mowableCells > 0 ? (ctx.inRangeCells / ctx.mowableCells) * 100 : 100;
    case 'trim': {
      const den = ctx.trimCells + ctx.grid.weedCount;
      return den > 0 ? ((ctx.inRangeTrim + ctx.weedsCleared) / den) * 100 : 100;
    }
    case 'blow':
      // Feature P6: with debris rules, beds and deck/step footprints count too
      // (and a catcher's pass clears), and leaves over the fence cost a nudge.
      if (ctx.debris) {
        if (!lc || lc.total <= 0) return 100;
        return ((lc.total - lc.dirty) / lc.total) * 100 - (lc.off > 0 ? OFF_PROPERTY_NUDGE_PCT : 0) - clipNudge(ctx);
      }
      return (lc && lc.total > 0 ? ((lc.total - lc.looseOnPaved) / lc.total) * 100 : 100) - clipNudge(ctx);
    case 'collect':
      return lc && lc.total > 0 ? ((lc.locked + lc.bagged) / lc.total) * 100 : 100;
    case 'ground-check': {
      const total = ctx.objects.length;
      if (!total) return 100;
      let n = 0;
      // Mulched counts as resolved (it's gone — and charged for in result()).
      for (const o of ctx.objects) if (o.state !== 'ground') n++;
      return (n / total) * 100;
    }
  }
  return 0;
}

/** Feature P9: clippings the trimmer threw onto the paving count against a clear until blown. */
function clipNudge(ctx: Ctx): number {
  return ctx.blowBack ? blowBackNudge(ctx.clipList.length) : 0;
}

function updateTasks(ctx: Ctx, emit: boolean): void {
  const needLeaves = ctx.tasks.some((t) => t.kind === 'blow' || t.kind === 'collect');
  const lc = needLeaves ? leafCounts(ctx) : null;
  let all = ctx.tasks.length > 0;
  for (const t of ctx.tasks) {
    t.pct = Math.min(100, Math.max(0, taskPct(ctx, t.kind, lc)));
    if (!t.done && t.pct >= t.target - 1e-9) {
      t.done = true;
      if (emit) ctx.events.push({ type: 'task-complete', task: t.kind });
    }
    if (!t.done) all = false;
  }
  if (all && !ctx.finished && emit) {
    ctx.finished = true;
    ctx.events.push({ type: 'all-done' });
  }
}

function result(ctx: Ctx, abandonedIn: boolean): JobResult {
  const abandoned = abandonedIn || !ctx.finished;
  const tasks = ctx.tasks.map((t) => ({ ...t }));
  const thoroughness = tasks.length ? tasks.reduce((s, t) => s + t.pct, 0) / tasks.length : 100;
  const fee = abandoned ? Math.round((ctx.contract.feeAud * thoroughness) / 100) : ctx.contract.feeAud;
  const stars = starsFor(ctx.elapsedS, ctx.parS, abandoned);
  const tp = abandoned ? 0 : tipPct(thoroughness);
  const tipAud = Math.round((fee * tp) / 100);
  const mulchedKinds = ctx.objects.filter((o) => o.state === 'mulched').map((o) => o.kind);
  const mulched = mulchedKinds.length;
  // Feature P1: a major hit (car, ute, pool) bills MAJOR_HIT_PENALTY_AUD instead of
  // OBSTACLE_PENALTY_AUD. With no major hits this is the original formula exactly.
  const majorHits = ctx.hitLog.filter((h) => h.damage === 'major').length;
  const penaltiesAud = (ctx.obstacleHits - majorHits) * OBSTACLE_PENALTY_AUD + majorHits * MAJOR_HIT_PENALTY_AUD
    + ctx.violations * VIOLATION_PENALTY_AUD + mulched * MULCH_PENALTY_AUD;
  const m = ctx.mower;
  // A walk-only job's parked stand-in mower wasn't brought, so it isn't billed wear.
  const gear: OwnedEquipment[] = ctx.loadout.includes('mower')
    ? [{ ...ctx.mowerOwned, engineCondition: m.engineCondition, bladeCondition: m.bladeCondition, fuelL: m.fuelL }]
    : [];
  for (const k of TOOL_ORDER) {
    const s = ctx.tools[k];
    if (s) gear.push({ ...s.owned });
  }
  // Feature P9: wallets handed back, and the one-off extra mile (never more than once a job).
  const bloke = ctx.groundItems ? honestBloke(ctx.objects) : { wallets: 0, aud: 0 };
  const extra = ctx.extraMile && extraMileEarned({
    abandoned, trimTask: ctx.specs.some((t) => t.kind === 'trim'), trimmed: ctx.ringTrimmed, weedsCleared: ctx.weedsCleared,
    trimCells: ctx.trimCells, weedCells: ctx.grid.weedCount,
  });
  const bonus = { honestBlokeAud: bloke.aud, extraMileAud: extra ? EXTRA_MILE_AUD : 0 };
  const r: JobResult = {
    contractId: ctx.contract.id,
    layoutKey: ctx.contract.layoutKey,
    abandoned,
    seconds: ctx.elapsedS,
    parSeconds: ctx.parS,
    stars,
    tasks,
    thoroughness,
    feeAud: fee,
    tipPct: tp,
    tipAud,
    obstacleHits: ctx.obstacleHits,
    violations: ctx.violations,
    hits: ctx.hitLog.map((h) => ({ ...h })),
    violationZones: ctx.violationZones.slice(),
    mulched,
    mulchedKinds,
    penaltiesAud,
    totalAud: Math.max(0, fee + tipAud + bonusAud(bonus) - penaltiesAud),
    repPoints: repForStars(stars) + (extra ? EXTRA_MILE_REP : 0),
    fuelUsedL: ctx.fuelUsedL,
    gear,
  };
  if (bloke.wallets > 0) {
    r.wallets = bloke.wallets;
    r.honestBlokeAud = bloke.aud;
  }
  if (extra) {
    r.extraMileAud = EXTRA_MILE_AUD;
    r.extraMileRep = EXTRA_MILE_REP;
  }
  if (ctx.groundItems && mulched) r.mulchedNames = ctx.objects.filter((o) => o.state === 'mulched').map((o) => groundName(o));
  // Feature P6: leaves over the fence on a clear job (the nudge is already in the task pct).
  if (ctx.debris && ctx.tasks.some((t) => t.kind === 'blow')) {
    const off = leafCounts(ctx).off;
    if (off > 0) r.offProperty = { leaves: off, nudgePct: OFF_PROPERTY_NUDGE_PCT };
  }
  // Feature P5: the stripe score rides along for information only; money is
  // untouched here (the bonus is `withStripeBonus`, applied by the shell).
  if (ctx.loadout.includes('mower') && ctx.specs.some((t) => t.kind === 'mow')) {
    const score = stripeScore(ctx.grid, {
      deckCells: ctx.mower.deckCells,
      islands: stripeIslands(ctx.layout, ctx.grid),
      front: frontOfHouse(ctx.layout, ctx.grid),
    });
    if (score !== null) r.stripeScore = score;
    // Crossed stripes: what the lawn reads as (plain stripes, a checkerboard, …).
    if (ctx.grid.stripe2 && score !== null) {
      const p = stripePattern(ctx.grid, { deckCells: ctx.mower.deckCells, gameScale: ctx.gs });
      if (p) r.stripePattern = p;
    }
  }
  return r;
}
